import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { recipientReminderBundleSchema } from "./shapes";

/**
 * Prepare Collection Reminder
 *
 * Receives a single assignee's reminder bundle (one fan-out branch from
 * Query Due Collection Follow-ups), renders the due-follow-up digest
 * title/body, and resolves that recipient's preferred delivery channels from
 * their saved notification preferences (falling back to `fallbackChannels`).
 *
 * Outputs map directly onto the input sockets of the Send Announcement step
 * running in input-driven mode: `recipientUserIds`, `title`, `body`, `channels`.
 */
export const prepareCollectionReminderStep = defineStepConfig({
  type: "prepare_collection_reminder",
  name: "Prepare Collection Reminder",
  description:
    "Render the due-follow-up digest for one assignee and resolve their " +
    "channels. Feeds Send Announcement (input-driven mode).",
  category: "Data Transform",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    recipientReminder: recipientReminderBundleSchema.describe(
      "One assignee's reminder bundle. Connect the 'reminders' array output " +
        "of Query Due Collection Follow-ups here to fan out per recipient.",
    ),
  }),

  outputSchema: z.object({
    recipientUserIds: z
      .array(z.string().uuid())
      .describe("Single-element list: the assignee to notify."),
    title: z.string().describe("Digest headline."),
    body: z.string().describe("Digest body listing the stale threads."),
    channels: z
      .array(z.enum(["in_app", "email"]))
      .describe("The recipient's resolved delivery channels."),
  }),

  configSchema: z.object({
    fallbackChannels: z
      .array(z.enum(["in_app", "email"]))
      .min(1)
      .default(["in_app"])
      .describe(
        "Channels used when the recipient has not saved a collection-reminder " +
          "channel preference. Defaults to in-app only; add 'email' to also " +
          "send email (via the Send Announcement v2 sender).",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
